import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { isVideoUrl } from '../utils/mediaUtils';

interface MediaDisplayProps {
  src: string;
  alt: string;
  onClick?: () => void;
  sx?: any;
  showPlayButton?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
}

const MediaDisplay: React.FC<MediaDisplayProps> = ({
  src,
  alt,
  onClick,
  sx = {},
  showPlayButton = true,
  autoPlay = false,
  muted = true,
  loop = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideo = isVideoUrl(src);

  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the parent onClick
    
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
    
    // Still call the parent onClick if provided
    if (onClick) {
      onClick();
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  if (isVideo) {
    return (
      <Box 
        sx={{ position: 'relative', ...sx }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <video
          ref={videoRef}
          src={src}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            cursor: 'pointer',
          }}
          onClick={handleVideoClick}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onError={(e) => {
            console.error('Video failed to load:', src, e);
          }}
        />
        
        {/* Play/Pause overlay button */}
        {showPlayButton && (isHovered || !isPlaying) && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              borderRadius: '50%',
              width: 60,
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                transform: 'translate(-50%, -50%) scale(1.1)',
              },
            }}
            onClick={handleVideoClick}
          >
            {isPlaying ? (
              <PauseIcon sx={{ color: 'white', fontSize: 30 }} />
            ) : (
              <PlayArrowIcon sx={{ color: 'white', fontSize: 30 }} />
            )}
          </Box>
        )}
        
        {/* Video controls hint */}
        {isVideo && !isPlaying && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 1,
              fontSize: '0.75rem',
              pointerEvents: 'none',
            }}
          >
            Click to play
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onClick={onClick}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        cursor: onClick ? 'pointer' : 'default',
        ...sx,
      }}
      onError={(e) => {
        console.error('Image failed to load:', src, e);
      }}
    />
  );
};

export default MediaDisplay; 